import { useEffect, useState } from "react";

import { obfuscatePII, useDemoMode } from "../lib/demoMode";
import { asKpiFormat, formatValue, inferFormatFromName, type KpiFormat } from "../lib/format";
import { humanize } from "../lib/labels";
import { type ManifestWidget, type PivotMeasure } from "../lib/manifest";
import { evaluateAst, parseMeasureExpression, uniqueTerms } from "../lib/measureExpression";
import {
  coverageGate,
  fetchAllGroups,
  orderPivotGroups,
  PIVOT_TRUNCATED_COPY,
  PivotTruncatedError,
  pivotKey,
  pivotKeyParts,
  pivotRowColumns,
} from "../lib/pivotShape.mjs";
import {
  applyFilters,
  applyNowCutoff,
  applyTimeline,
  canonicalColumnName,
  resolveSourceName,
} from "../lib/queries";
import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";
import { scopeTenantTable } from "../lib/tenantScope.mjs";

/**
 * Measure-table widget (manifest type=pivot_table, declares `rows` +
 * `measures`). Renders one row per distinct value of `widget.rows` with
 * one column per measure. Cell values are the aggregate of each measure
 * computed within the row group, with widget filters + timeline applied.
 *
 * Currently used by 1.2 Revenue by Operation on the DWS Revenue tab:
 *   rows: equipment_type
 *   measures: [Earned, Billed, Disposal Cost, Net Margin, Margin %]
 *
 * A true 2D pivot (rows + columns + single measure) would render via a
 * different component; this one fits the "horizontal KPI table" shape
 * that the DWS manifest actually uses.
 */

export interface MeasurePivotTableProps {
  widget: ManifestWidget;
  rows: string;
  measures: PivotMeasure[];
  format: KpiFormat;
}

interface RowGroup {
  key: string;
  values: Record<string, number | null>;
}

/**
 * Wave 1 Disposal tab: the opt-in pivot shaping (lib/pivotShape.mjs). A pivot
 * that declares none of these renders exactly as it did before.
 */
interface PivotShapeOpts {
  rows_then?: string;
  sort?: { measure?: string; direction?: string; key_direction?: string };
  limit?: number;
  /** Header text for a grouping column, e.g. `{division_name: Division}`. */
  row_labels?: Record<string, string>;
}

/** MER-720 on a ratio measure: `num` over `of` must reach `floor` per row. */
interface MeasureCoverage {
  num: string;
  of: string;
  floor: number;
}

function coverageOf(measure: PivotMeasure): MeasureCoverage | null {
  const c = (measure as PivotMeasure & { coverage?: Partial<MeasureCoverage> }).coverage;
  if (!c || typeof c.num !== "string" || typeof c.of !== "string") return null;
  if (typeof c.floor !== "number") return null;
  return { num: c.num, of: c.of, floor: c.floor };
}

// MER-286: expression parser lives in lib/measureExpression.ts. Supports
// SIMPLE `agg(col) op agg(col)` plus COMPOUND `(agg(col) op agg(col)) op
// agg(col)`. The compound form is required for weighted-ratio measures
// like Margin % = `(sum(rev) - sum(cost)) / sum(rev)`. Without it,
// authors fell back to `aggregation: sum` on a `_pct` column, which sums
// fractions across rows and inflates Margin % linearly with row count.

function measureLabel(measure: PivotMeasure, idx: number): string {
  return measure.label ?? `Measure ${idx + 1}`;
}

/**
 * Resolve a per-measure format (MER-180). Priority:
 *   1. Explicit `measure.display_format` from the manifest.
 *   2. Naming-heuristic inference from column / label.
 *   3. Widget-level fallback passed in by the renderer.
 *
 * For expression measures (Net Margin = sum(revenue) - sum(cost)), the
 * column is absent but the label usually carries enough signal
 * ("Margin", "P&L", "Net") to land on currency. Falls back cleanly when
 * the label gives no hint.
 */
function measureFormat(measure: PivotMeasure, fallback: KpiFormat): KpiFormat {
  if (measure.display_format) {
    return asKpiFormat(measure.display_format);
  }
  const inferred = inferFormatFromName(measure.column, measure.label);
  return inferred ?? fallback;
}

async function runGroupedAggregate(
  table: string,
  rowCols: string[],
  column: string,
  agg: string,
  widget: ManifestWidget,
  tenantId: string,
  paged: boolean,
): Promise<Map<string, number | null>> {
  const supabase = getSupabase();
  // PostgREST aggregate with grouping: select the row column + the
  // aggregate column. PostgREST infers GROUP BY from the unaggregated
  // columns in the select list.
  //
  // Wave 1 Disposal tab: PostgREST caps every response at its max-rows, one
  // row per group here. A shaped pivot (sorted, capped or two-column) pages
  // through every group under a stable order before it ranks; any other pivot
  // reads once and refuses a full page as cut. Either way a cut read throws
  // PivotTruncatedError and the table is withheld, never ranked on a subset.
  const fetchPage = async (from: number, to: number): Promise<Record<string, unknown>[]> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let q: any = scopeTenantTable(
      (supabase.from(table) as any).select(`${rowCols.join(",")},${column}.${agg}()`),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyTimeline(q, widget);
    q = applyNowCutoff(q, widget, table);
    if (paged) {
      for (const c of rowCols) q = q.order(c, { ascending: true });
      q = q.range(from, to);
    }
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
  };
  const data = await fetchAllGroups(fetchPage, { paged });
  const out = new Map<string, number | null>();
  for (const row of data) {
    // One grouping column keeps its plain value as the key (every pivot before
    // the Disposal tab); two are encoded together (pivotShape.mjs).
    const key =
      rowCols.length === 1 ? String(row[rowCols[0]] ?? "") : pivotKey(rowCols.map((c) => row[c]));
    const v = row[agg] ?? row[column];
    if (v === null || v === undefined) {
      out.set(key, null);
      continue;
    }
    const n = Number(v);
    out.set(key, Number.isNaN(n) ? null : n);
  }
  return out;
}

async function resolveMeasure(
  measure: PivotMeasure,
  rowCols: string[],
  table: string,
  widget: ManifestWidget,
  tenantId: string,
  paged: boolean,
): Promise<Map<string, number | null>> {
  if (measure.column && measure.aggregation) {
    return runGroupedAggregate(
      table,
      rowCols,
      canonicalColumnName(measure.column),
      measure.aggregation,
      widget,
      tenantId,
      paged,
    );
  }
  if (measure.expression) {
    const ast = parseMeasureExpression(measure.expression);
    if (!ast) return new Map();
    const terms = uniqueTerms(ast);
    const aggregated = await Promise.all(
      terms.map((t) =>
        runGroupedAggregate(
          table,
          rowCols,
          canonicalColumnName(t.column),
          t.agg,
          widget,
          tenantId,
          paged,
        ),
      ),
    );
    const out = new Map<string, number | null>();
    const keys = new Set<string>();
    aggregated.forEach((m) => m.forEach((_v, k) => keys.add(k)));
    const coverage = coverageOf(measure);
    const [covered, whole] = coverage
      ? await Promise.all([
          runGroupedAggregate(
            table,
            rowCols,
            canonicalColumnName(coverage.num),
            "sum",
            widget,
            tenantId,
            paged,
          ),
          runGroupedAggregate(
            table,
            rowCols,
            canonicalColumnName(coverage.of),
            "sum",
            widget,
            tenantId,
            paged,
          ),
        ])
      : [null, null];
    for (const k of keys) {
      const values = aggregated.map((m) => m.get(k));
      const value = evaluateAst(ast, values);
      out.set(
        k,
        coverage && covered && whole
          ? coverageGate(value, covered.get(k), whole.get(k), coverage.floor)
          : value,
      );
    }
    return out;
  }
  return new Map();
}

export default function MeasurePivotTable(props: MeasurePivotTableProps) {
  const { widget, rows: rowDim, measures, format } = props;
  // MER-291: obfuscate PII row labels (route_id, customer_id, driver_id)
  // in demo mode. Hook must run unconditionally.
  const demoMode = useDemoMode();
  const [state, setState] = useState<{
    groups: RowGroup[];
    measureErrors: (string | null)[];
    isLoading: boolean;
    error: string | null;
    /** Wave 1: not every group could be read, so the table is withheld. */
    truncated?: boolean;
  }>({ groups: [], measureErrors: [], isLoading: true, error: null });

  const shape = widget as ManifestWidget & PivotShapeOpts;
  const rowCols = pivotRowColumns({ rows: rowDim, rows_then: shape.rows_then }).map((c) =>
    canonicalColumnName(c),
  );
  const shaped =
    rowCols.length > 1 ||
    Boolean(shape.sort?.measure) ||
    Boolean(shape.sort?.key_direction) ||
    typeof shape.limit === "number";
  const tenantId = useTenantId();

  useEffect(() => {
    if (!tenantId) {
      setState({ groups: [], measureErrors: [], isLoading: true, error: null });
      return;
    }
    const boundTenantId = tenantId;
    let cancelled = false;
    setState({ groups: [], measureErrors: [], isLoading: true, error: null });

    async function run() {
      if (!widget.source) {
        if (!cancelled)
          setState({ groups: [], measureErrors: [], isLoading: false, error: "Missing source" });
        return;
      }
      const table = resolveSourceName(widget.source);
      // Resolve each measure independently. A column-not-found error in
      // one measure shouldn't nuke the whole pivot; the other columns
      // still render. Per-measure errors surface as `err` in their cells
      // with the original message in the cell title attribute.
      const settled = await Promise.allSettled(
        measures.map((m) => resolveMeasure(m, rowCols, table, widget, boundTenantId, shaped)),
      );
      if (cancelled) return;
      // A measure that could not read every group withholds the whole table:
      // ranking or showing a subset would state a partial set as the whole.
      if (settled.some((r) => r.status === "rejected" && r.reason instanceof PivotTruncatedError)) {
        setState({
          groups: [],
          measureErrors: [],
          isLoading: false,
          error: null,
          truncated: true,
        });
        return;
      }
      const measureMaps: Map<string, number | null>[] = [];
      const measureErrors: (string | null)[] = [];
      for (const s of settled) {
        if (s.status === "fulfilled") {
          measureMaps.push(s.value);
          measureErrors.push(null);
        } else {
          measureMaps.push(new Map());
          measureErrors.push(s.reason instanceof Error ? s.reason.message : String(s.reason));
        }
      }
      // Union of all row keys across measure result sets.
      const keys = new Set<string>();
      for (const m of measureMaps) {
        for (const k of m.keys()) keys.add(k);
      }
      const unordered: RowGroup[] = Array.from(keys).map((k) => {
        const values: Record<string, number | null> = {};
        measures.forEach((m, i) => {
          values[measureLabel(m, i)] = measureMaps[i].get(k) ?? null;
        });
        return { key: k, values };
      });
      const groups: RowGroup[] = shaped
        ? orderPivotGroups(unordered, shape.sort, shape.limit)
        : unordered.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
      setState({ groups, measureErrors, isLoading: false, error: null });
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, tenantId]);

  const cardStyle: React.CSSProperties = {
    background: "var(--bg-card)",
    border: "1px solid var(--border-subtle)",
    borderRadius: 12,
    overflow: "hidden",
    padding: 20,
  };

  if (state.isLoading) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)" }}>Loading…</div>;
  }
  if (state.error) {
    return (
      <div style={{ ...cardStyle, color: "var(--flag-red)" }} title={state.error}>
        Widget data unavailable: {state.error}
      </div>
    );
  }
  if (state.truncated) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)" }}>{PIVOT_TRUNCATED_COPY}</div>;
  }
  if (state.groups.length === 0) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)" }}>No rows.</div>;
  }

  // MER-377: same header/row treatment as PresentedTable (the tabular /
  // drill-down surface) -- bold labels under the brand green-to-teal bar,
  // faint alternating row tint. Kept in sync by eye rather than a shared
  // constant because this component's header also carries the sticky-column
  // rowDim label PresentedTable doesn't have.
  const headerStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-muted)",
    borderBottom: "2px solid var(--brand-teal)",
  };
  const cellStyle: React.CSSProperties = {
    padding: "10px 12px",
    fontSize: 14,
    borderBottom: "1px solid var(--border-subtle)",
  };
  const stripedRowStyle: React.CSSProperties = { background: "var(--bg-app)" };

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {(rowCols.length > 1 ? [rowDim, shape.rows_then ?? ""] : [rowDim]).map((col) => (
              <th key={col} style={headerStyle}>
                {shape.row_labels?.[col] ?? humanize(col)}
              </th>
            ))}
            {measures.map((m, i) => (
              <th key={i} style={{ ...headerStyle, textAlign: "right" }}>
                {measureLabel(m, i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state.groups.map((g, gi) => {
            // MER-441: a row with $0 disposal cost yields a meaningless margin
            // (Side Load shows 100% from $0 disposal attributed). Gate the
            // percent (margin) cells of such a row to "n/a", but keep the row's
            // dollar figures, including a real negative billed (AJ's credits).
            // Detect the disposal COST measure (currency), not a disposal *_pct
            // measure (burden/coverage %) -- those are valid regardless of cost,
            // and matching one would gate margins on the wrong column's zero.
            const disposalLabel = measures
              .map((m, i) => ({ lab: measureLabel(m, i), fmt: measureFormat(m, format) }))
              .find((m) => /disposal/i.test(m.lab) && m.fmt !== "percent")?.lab;
            const disposalVal = disposalLabel ? g.values[disposalLabel] : undefined;
            const disposalZero = disposalVal === 0 || disposalVal === null;
            return (
              <tr key={g.key} style={gi % 2 === 1 ? stripedRowStyle : undefined}>
                {rowCols.length > 1 ? (
                  pivotKeyParts(g.key).map((part, pi) => (
                    <td key={`k${pi}`} style={cellStyle}>
                      {humanize(String(obfuscatePII(part, rowCols[pi], demoMode) ?? part))}
                    </td>
                  ))
                ) : (
                  <td style={cellStyle}>
                    {humanize(String(obfuscatePII(g.key, rowDim, demoMode) ?? g.key))}
                  </td>
                )}
                {measures.map((m, i) => {
                  const label = measureLabel(m, i);
                  const v = g.values[label];
                  const err = state.measureErrors[i];
                  const cellFormat = measureFormat(m, format);
                  const gateMargin = disposalZero && cellFormat === "percent";
                  return (
                    <td
                      key={i}
                      style={{
                        ...cellStyle,
                        textAlign: "right",
                        fontWeight: 500,
                        // MER-462: a failed measure renders muted "n/a", never
                        // raw red "err" in front of a customer (the message
                        // stays in the title attribute for debugging).
                        color: err ? "var(--text-muted)" : undefined,
                      }}
                      title={err ?? undefined}
                    >
                      {err || gateMargin ? "n/a" : formatValue(v, cellFormat)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
